import { useMemo } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import { Download } from "components/icons/generated";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import useCanDownloadStackState from "shared/Stack/access/useCanDownloadStackState";
import useDownloadStackState from "shared/Stack/useDownloadStackState";
import { downloadFileFromURL } from "utils/file";
import { StackContext } from "views/Stack/Context";

const NO_EXTERNAL_ACCESS_MESSAGE =
  "External state access must be enabled on the stack to download state";

const StackStateDownloadButton = () => {
  const { stack } = useTypedContext(StackContext);
  const { reportError } = useTypedContext(FlashContext);

  const canDownloadStackState = useCanDownloadStackState()(stack);

  const [getStateDownloadUrl, { loading }] = useDownloadStackState();

  const vendorConfig =
    stack.vendorConfig?.__typename === "StackConfigVendorTerraform"
      ? stack.vendorConfig
      : undefined;
  const externalStateAccessEnabled = vendorConfig?.externalStateAccessEnabled;

  const canDownload = !!(canDownloadStackState.result && externalStateAccessEnabled);

  const tooltipMessage = useMemo(() => {
    if (!canDownloadStackState.result) {
      return canDownloadStackState.message;
    }
    if (!externalStateAccessEnabled) {
      return NO_EXTERNAL_ACCESS_MESSAGE;
    }
    return undefined;
  }, [canDownloadStackState, externalStateAccessEnabled]);

  const onDownloadClick = () => {
    getStateDownloadUrl({ input: { stackId: stack.id, version: null } }, (data) => {
      if (data?.stateDownloadUrl?.url) {
        downloadFileFromURL(data.stateDownloadUrl.url, `${stack.name}.tfstate`);
      } else {
        reportError({ message: "Could not generate a download link" });
      }
    });
  };

  return (
    <Tooltip
      active={!canDownload}
      on={(props) => (
        <Button
          {...props}
          disabled={!canDownload}
          loading={loading}
          onPress={onDownloadClick}
          startIcon={Download}
          variant="secondary"
        >
          Download state
        </Button>
      )}
    >
      {tooltipMessage}
    </Tooltip>
  );
};

export default StackStateDownloadButton;
